import * as React from 'react'
import * as RadioGroup from 'primitives/radio-group'
import { useUserQuery } from 'auth/hooks'
import { tw } from 'utils/classnames'
import { useTranslations } from 'next-intl'
import type { PDPAsset, PDPCategory } from 'cf-grpc/creativefabrica/content/v1/types_pb'

import {
  useSelectedAssetState,
  useWasDownloadedState,
} from '@web/app/[locale]/(web)/prod/[slug]/product-asset-context'
import { getAssetVariantLabel, getAssetsDownloadOptions } from './util'

interface DetailsWithAccessProps {
  className?: string
  category: PDPCategory | undefined
  assets: PDPAsset[]
}

export function DetailsWithAccess({ className, assets, category }: DetailsWithAccessProps) {
  const t = useTranslations('prod.product_description_page.buy')
  const [, setSelectedAsset] = useSelectedAssetState()
  const [wasDownloaded] = useWasDownloadedState()
  const { isPending, data } = useUserQuery()
  const isLoggedIn = !isPending && !!data?.me?.user?.id
  const downloadableAssets = getAssetsDownloadOptions(category?.root?.id ?? '', assets)
  // oxlint-disable-next-line unicorn/prefer-at
  const defaultVariantId = downloadableAssets[downloadableAssets.length - 1]?.id
  // oxlint-disable-next-line unicorn/prefer-at
  const defaultAssetFilePath = downloadableAssets[downloadableAssets.length - 1]?.filePath
  const showOptions = assets.length > 1

  if (!isLoggedIn || wasDownloaded) {
    return null
  }

  return (
    <RadioGroup.Root
      className={tw('mb-6 flex flex-col gap-4 p-4 pb-0 ', assets.length <= 1 && 'mb-2', className)}
      defaultValue={defaultVariantId}
      aria-label="Select image quality"
      onValueChange={value => {
        const selectedAsset = assets.find(asset => asset.id === value)
        setSelectedAsset({
          assetId: selectedAsset?.id ?? defaultVariantId,
          assetPath: selectedAsset?.filePath ?? defaultAssetFilePath,
        })
      }}
    >
      {showOptions
        ? downloadableAssets.map(asset => (
            <div className="flex items-center" key={asset.id}>
              <RadioGroup.Item
                className={tw(
                  'bg-cf-white flex size-4 items-center justify-center rounded-full border border-opacity-50',
                )}
                value={asset.id}
                id={asset.id}
                aria-label={getAssetVariantLabel(asset.variant, t)}
              >
                <RadioGroup.Indicator className="after:bg-cf-black relative flex h-full w-full items-center justify-center after:block after:size-[10px] after:rounded-[50%] after:content-['']" />
              </RadioGroup.Item>
              <label
                className="text-16 flex cursor-pointer gap-2 pl-[15px] leading-none text-white"
                htmlFor={asset.id}
              >
                <b className="font-medium">{getAssetVariantLabel(asset.variant, t)}</b>
                <span className="text-12">
                  {asset.resolutionWidth} X {asset.resolutionHeight} px
                </span>
              </label>
            </div>
          ))
        : null}
    </RadioGroup.Root>
  )
}
